import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useViewportDom } from 'craft/state';

const MIN_TABS = 1;
const MAX_TABS = 8;
const TAB_WIDTH = 60;
const TAB_GAP = 10;

const noArrowsState = {
  shouldShowArrows: false,
  isLeftArrowActive: false,
  isRightArrowActive: false,
};

export const useFloatingMenuNavigation = ({
  tabs,
  wrapperRef,
  activeTabIndex,
}: {
  tabs: string[];
  wrapperRef: RefObject<HTMLDivElement | null>;
  activeTabIndex?: number;
}) => {
  const { viewportWrapperEl } = useViewportDom();
  const latestMaxVisibleTabs = useRef(MAX_TABS);
  const [firstIndex, setFirstIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(MAX_TABS - 1);

  const [visibleTabs, setVisibleTabs] = useState<string[]>(tabs.slice(firstIndex, lastIndex + 1));

  const latestArrowState = useRef<{
    shouldShowArrows: boolean;
    isLeftArrowActive: boolean;
    isRightArrowActive: boolean;
  }>(noArrowsState);

  const [arrowsState, setArrowsState] = useState(latestArrowState.current);

  const updateVisibleTabs = () => {
    if (!isEqual(visibleTabs, tabs.slice(firstIndex, lastIndex + 1))) {
      setVisibleTabs(tabs.slice(firstIndex, lastIndex + 1));
    }
  };

  /**
   * Recalculate the state of the navigation arrows
   */
  const recalculateArrowStates = useCallback(() => {
    const shouldShowArrows = tabs.length > 1 && tabs.length > visibleTabs.length;

    if (!shouldShowArrows) {
      if (!isEqual(latestArrowState.current, noArrowsState)) {
        latestArrowState.current = noArrowsState;
        setArrowsState(noArrowsState);
      }
      return;
    }

    const newArrowsState = {
      shouldShowArrows,
      isLeftArrowActive: firstIndex > 0,
      isRightArrowActive: lastIndex < tabs.length - 1,
    };

    if (!isEqual(latestArrowState.current, newArrowsState)) {
      // only update state if it's different
      latestArrowState.current = newArrowsState;
      setArrowsState(newArrowsState);
    }
  }, [tabs, visibleTabs, firstIndex, lastIndex]);

  const throttleRecalculateArrowStates = useCallback(
    throttle(() => {
      recalculateArrowStates();
    }, 500),
    [recalculateArrowStates],
  );

  /**
   * Recalculate the number of visible tabs based on the available width
   */
  const recalculateVisibleTabs = useCallback(() => {
    const floatingMenuEl = document.getElementById('floating-menu');

    if (!wrapperRef.current || !floatingMenuEl || !viewportWrapperEl) {
      return;
    }

    const { paddingLeft, paddingRight } = window.getComputedStyle(floatingMenuEl);
    const actionButtonsEl = document.getElementById('floating-menu-action-buttons');

    const maxAvailableWidth =
      viewportWrapperEl.clientWidth * 0.8 -
      parseFloat(paddingLeft) -
      parseFloat(paddingRight) -
      (actionButtonsEl?.clientWidth || 0) -
      50;

    const currentTotalWidth = wrapperRef.current.scrollWidth;

    if (latestMaxVisibleTabs.current === MAX_TABS && currentTotalWidth <= maxAvailableWidth) {
      // all 8 tabs are visible and fit, no need to recalculate
      return;
    }

    const newMaxTabs = Math.min(
      Math.max(MIN_TABS, Math.floor(maxAvailableWidth / (TAB_WIDTH + TAB_GAP))),
      MAX_TABS,
    );

    if (newMaxTabs !== latestMaxVisibleTabs.current) {
      latestMaxVisibleTabs.current = newMaxTabs;
      setLastIndex(firstIndex + newMaxTabs - 1);
    }
  }, [visibleTabs, firstIndex]);

  const throttleRecalculateVisibleTabs = useCallback(
    throttle(() => {
      recalculateVisibleTabs();
    }, 200),
    [recalculateVisibleTabs],
  );

  /**
   * Shift one tab to the left, if we're not at the first tab
   */
  const navigateLeft = useCallback(
    (offset = 1) => {
      if (firstIndex === 0) {
        return;
      }

      setFirstIndex((prevState) => prevState - offset);
      setLastIndex((prevState) => prevState - offset);
    },
    [firstIndex],
  );

  /**
   * Shift one tab to the right, if we're not at the last tab
   */
  const navigateRight = useCallback(
    (offset = 1) => {
      if (lastIndex === tabs.length - 1) {
        return;
      }

      setFirstIndex((prevState) => prevState + offset);
      setLastIndex((prevState) => prevState + offset);
    },
    [lastIndex, tabs.length],
  );

  const onAutoScrollToActiveTab = useCallback(() => {
    if (
      typeof activeTabIndex === 'undefined' ||
      (activeTabIndex >= firstIndex && activeTabIndex <= lastIndex)
    ) {
      return;
    }

    if (activeTabIndex < firstIndex) {
      navigateLeft(firstIndex - activeTabIndex);
    } else {
      navigateRight(activeTabIndex - lastIndex);
    }
  }, [activeTabIndex, firstIndex, lastIndex]);

  /**
   * When the active tab changes, make sure it's fully in view in the nav
   */
  useEffect(() => {
    onAutoScrollToActiveTab();
  }, [activeTabIndex]);

  /**
   * When navigation shifts, or tabs change, update the visible tabs
   */
  useEffect(() => {
    updateVisibleTabs();
  }, [tabs, firstIndex, lastIndex]);

  /**
   * When visible tabs are updated, or when tabs are added/deleted, recalculate the arrow states
   */
  useEffect(() => {
    throttleRecalculateArrowStates();
  }, [visibleTabs, tabs.length]);

  /**
   * When the canvas resizes, recalculate the number of visible tabs
   * Any changes to the visible tabs will trigger a recalculation of the arrow states
   */
  useEffect(() => {
    if (!viewportWrapperEl) {
      return () => null;
    }

    // make sure to recalculate nav arrows when the viewportContainer is resized
    const observer = new ResizeObserver(throttleRecalculateVisibleTabs);
    observer.observe(viewportWrapperEl);

    return () => {
      observer.disconnect();
    };
  }, [throttleRecalculateVisibleTabs]);

  return {
    visibleTabs,
    navigateRight,
    navigateLeft,
    arrowsState,
  };
};
